import { string, ValidationError } from 'yup';
import { USERNAME_MAX_LENGTH, USERNAME_MIN_LENGTH } from 'app-constants';
import { doesUserExist } from 'services/api/firestore';
import { Artist } from 'types';

type Args = {
  expectedWidth?: number;
  expectedHeight?: number;
};

type ArtistDisplayNameValidatorOptions = {
  removeArtistDisplayNameLengthValidators?: boolean;
};

export const MAX_CHARACTERS_LENGTH = {
  USERNAME_MAX_LENGTH: 50,
  FIRST_NAME_LAST_NAME_MAX_LENGTH: 50,
};

export const resolutionStringValidator = (args?: Args) => {
  return string().matches(
    new RegExp(`${args?.expectedWidth || '\\d+'}x${args?.expectedHeight || '\\d+'}`),
  );
};

// Regex for multiple spaces of the string.
export const spacesValidator = (value: string) => !value.match(/ {2,}|'{2,}|-{2,}/);

export const puzzleNameValidator = () => {
  return string()
    .required('Please give your puzzle a name.')
    .matches(/^([a-zA-Z0-9 '-]+)$/, 'Please use English letters, numbers, dashes and apostrophes.')
    .test(
      'duplicate-specials',
      'Please avoid using multiple spaces, dashes and apostrophes',
      spacesValidator,
    )
    .min(3, 'Please use at least three characters for puzzle name.')
    .max(60, 'Please limit puzzle name to 60 characters');
};

/**
 * Function that uses yup validation to check if username (aka display name) is
 * within required limits.
 *
 * Use with yup validation.
 *
 * @example
 * object({
 *   username: artistDisplayNameValidator(),
 * })
 *
 * @todo Find a way to debounce this test while showing correct validation info.
 */
export const artistDisplayNameValidator = (
  artistData?: Artist,
  options?: ArtistDisplayNameValidatorOptions,
) => {
  return string().test({
    name: 'does-username-exist',
    message: 'Display name already in use, please try another one',
    test: (username, context) => {
      const oldValue = artistData?.username;
      try {
        /**
         * These checks were previously placed before this "does-username-exist" test function.
         * I could not find a way to check if any of them failed before attempting "does-username-exist" test function.
         * That resulted in calls to DB that are unnecessary. To prevent that, I moved these checks inside of this
         * function. From user's perspective everything should look and act the same.
         */
        let val = string()
          .required('Please enter your display name')
          .matches(/^[^@]+$/, 'You cannot use @ character.');

        if (!options || !options.removeArtistDisplayNameLengthValidators) {
          val = val
            .min(USERNAME_MIN_LENGTH, `Please use at least ${USERNAME_MIN_LENGTH} characters.`)
            .max(
              USERNAME_MAX_LENGTH,
              `Please use up to ${USERNAME_MAX_LENGTH} characters for display name`,
            );
        }

        val
          .test(
            'duplicate-specials',
            'Please avoid using multiple spaces, dashes and apostrophes',
            () => spacesValidator(username ?? ''),
          )
          .matches(/\w/, 'Please add at least 1 alphanumeric character.')
          .validateSync(username);
      } catch (error: unknown) {
        // Catch validation error message of previous steps and pass them as this test's message.
        if (error instanceof ValidationError)
          return context.createError({ message: error.message });

        // Just in case some other wild error occurs we want to be aware of it.
        throw error;
      }

      return username ? (oldValue === username ? true : doesUserExist(username) ?? false) : false;
    },
  });
};
